import { useState } from 'react'
import { ArrowRight, Check, Loader2, Upload, X } from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import { cn } from '../lib/cn'

type FileStatus = 'pending' | 'uploading' | 'done' | 'error'

interface UploadItem {
  id: string
  file: File
  status: FileStatus
  parsed?: number
  seeded?: number
  error?: string
}

interface UploadResp {
  parsed: number
  seeded: number
  total_in_db: number
  pending: number
}

interface Props {
  onComplete?: (totals: { parsed: number; seeded: number; uploaded: number }) => void
  buttonLabel?: string
  helpText?: React.ReactNode
}

export function MultiCsvUpload({ onComplete, buttonLabel, helpText }: Props) {
  const [items, setItems] = useState<UploadItem[]>([])
  const [drag, setDrag] = useState(false)
  const [busy, setBusy] = useState(false)
  const [topError, setTopError] = useState<string | null>(null)

  function addFiles(files: FileList | null) {
    if (!files) return
    const next: UploadItem[] = Array.from(files)
      .filter((f) => f.name.toLowerCase().endsWith('.csv'))
      .map((f) => ({
        id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2)}`,
        file: f,
        status: 'pending',
      }))
    setItems((prev) => [...prev, ...next])
  }

  function removeItem(id: string) {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }

  function patchItem(id: string, patch: Partial<UploadItem>) {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)))
  }

  async function uploadAll() {
    setBusy(true)
    setTopError(null)
    let totalParsed = 0
    let totalSeeded = 0
    let uploaded = 0
    const pending = items.filter((i) => i.status === 'pending' || i.status === 'error')
    for (const item of pending) {
      patchItem(item.id, { status: 'uploading', error: undefined })
      try {
        const fd = new FormData()
        fd.append('file', item.file)
        const res = await api.post<UploadResp>('/connections/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const { parsed, seeded } = res.data
        totalParsed += parsed
        totalSeeded += seeded
        uploaded += 1
        patchItem(item.id, { status: 'done', parsed, seeded })
      } catch (err) {
        const msg = apiErrorMessage(err)
        patchItem(item.id, { status: 'error', error: msg })
        setTopError(`${item.file.name}: ${msg}`)
      }
    }
    setBusy(false)
    if (uploaded > 0 && onComplete) {
      onComplete({ parsed: totalParsed, seeded: totalSeeded, uploaded })
    }
  }

  const hasUnfinished = items.some((i) => i.status === 'pending' || i.status === 'error')
  const canUpload = !busy && hasUnfinished

  return (
    <div className="space-y-3">
      {helpText && <div className="text-sm text-gray-400">{helpText}</div>}
      <label
        onDragOver={(e) => {
          e.preventDefault()
          setDrag(true)
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDrag(false)
          addFiles(e.dataTransfer.files)
        }}
        className={cn(
          'block border-2 border-dashed rounded-lg px-6 py-8 text-center cursor-pointer transition',
          drag ? 'border-accent bg-accent/5' : 'border-border hover:border-gray-500',
        )}
      >
        <input
          type="file"
          accept=".csv"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files)
            e.target.value = ''
          }}
        />
        <Upload className="mx-auto text-gray-500" size={20} />
        <div className="text-sm text-gray-300 mt-2">
          Drop CSV files here or click to choose. Multiple files OK — duplicate URLs are skipped.
        </div>
      </label>

      {items.length > 0 && (
        <ul className="space-y-1.5">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-3 border border-border rounded-md px-3 py-2 text-sm"
            >
              <div className="flex-1 min-w-0">
                <div className="text-gray-200 truncate">{item.file.name}</div>
                <div className="text-xs text-gray-500">
                  {(item.file.size / 1024).toFixed(0)} KB
                  {item.status === 'done' &&
                    ` · ${item.seeded} new of ${item.parsed} rows`}
                  {item.status === 'error' && item.error && ` · ${item.error}`}
                </div>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                {item.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="text-gray-500 hover:text-red-400"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                )}
                {item.status === 'uploading' && (
                  <Loader2 size={14} className="animate-spin text-gray-400" />
                )}
                {item.status === 'done' && <Check size={14} className="text-green-400" />}
                {item.status === 'error' && (
                  <span className="text-xs text-red-400">Failed</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-end pt-1">
        <button className="btn-primary" disabled={!canUpload} onClick={uploadAll}>
          {busy ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Uploading…
            </>
          ) : (
            <>
              {buttonLabel ??
                (items.length > 1
                  ? `Upload ${items.filter((i) => i.status !== 'done').length} files`
                  : 'Upload')}{' '}
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
      {topError && <div className="text-sm text-red-400">{topError}</div>}
    </div>
  )
}
